/**
 * Turning push notifications on for this browser.
 *
 * Everything here is about one device. Which notifications somebody wants is an
 * account-level setting and lives with the rest of them; whether *this* phone
 * buzzes is a property of this browser's permission and this browser's
 * subscription, and cannot be answered from the server alone.
 *
 * ## Why so much of this is feature detection
 *
 * Push is the least evenly supported thing in the app, and the failure modes
 * are all silent. A browser with no `PushManager` throws on the first property
 * access; Safari on iOS has the API present in an ordinary tab but refuses to
 * grant it until the site is on the Home Screen; a denied permission cannot be
 * re-requested by script at all and has to be undone in browser settings. Each
 * of those needs a different sentence in front of the user, so `pushState()`
 * distinguishes them rather than collapsing them into "unavailable".
 */

import { api } from "./api";
import type { PushSubscriptionPayload } from "../types";

/** Where this browser stands, in the terms the settings panel needs. */
export type PushAvailability =
  /** No service worker or no PushManager. Nothing to offer. */
  | "unsupported"
  /** iOS, in a tab. Real support, but only once Pulse is on the Home Screen. */
  | "needs-install"
  /** The server has no VAPID keys, so nothing can be sent. */
  | "not-configured"
  /** Blocked in browser settings; script cannot ask again. */
  | "denied"
  /** Available and off. */
  | "off"
  /** Available and on, for this browser. */
  | "on";

export interface PushState {
  availability: PushAvailability;
  /** Browsers other than this one that are signed up, per the server. */
  otherDevices: number;
}

/**
 * Whether the browser has the machinery at all.
 *
 * `PushManager` on the window is the honest test: Safari on iOS has had
 * `serviceWorker` for years while push arrived only in 16.4, so checking for
 * the worker alone reports support that is not there.
 */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Whether the page is running as an installed app rather than in a tab.
 *
 * Only consulted on iOS, where it is the difference between push working and
 * push being impossible. `navigator.standalone` is Safari's own long-standing
 * flag; the media query is the standard other browsers answer, and either being
 * true is enough.
 */
function installed(): boolean {
  const legacy = (navigator as { standalone?: boolean }).standalone === true;
  return legacy || window.matchMedia("(display-mode: standalone)").matches;
}

/**
 * iOS, including iPadOS pretending to be a Mac.
 *
 * The iPad has reported itself as a Mac since iPadOS 13, so the touch-point
 * check is what separates a real desktop Safari (where push works in a tab)
 * from an iPad (where it does not).
 */
function isIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return ua.includes("Macintosh") && navigator.maxTouchPoints > 1;
}

/**
 * Register the worker, or reuse the registration already there.
 *
 * `ready` rather than the promise `register()` returns, because a registration
 * that exists is not necessarily *active* yet, and subscribing against one that
 * is still installing fails. `ready` is exactly "there is an active worker".
 */
async function registration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

/**
 * What to show in settings.
 *
 * Reads the server (are there keys, how many devices) and the browser (is it
 * supported, is it permitted, is this one subscribed) and reconciles the two,
 * because either alone gives the wrong answer: the server cannot see that this
 * browser has revoked permission, and the browser cannot see that push has
 * never been configured.
 */
export async function pushState(): Promise<PushState> {
  const status = await api.pushStatus();

  if (!status.enabled) {
    return { availability: "not-configured", otherDevices: 0 };
  }

  if (!pushSupported()) {
    /* Other devices still worth reporting: somebody on a desktop that cannot do
       push should still see that their phone is signed up. */
    return { availability: "unsupported", otherDevices: status.devices };
  }

  if (isIOS() && !installed()) {
    return { availability: "needs-install", otherDevices: status.devices };
  }

  if (Notification.permission === "denied") {
    return { availability: "denied", otherDevices: status.devices };
  }

  const existing = await currentSubscription();

  return {
    availability: existing ? "on" : "off",
    /* The server's count includes this browser when it is subscribed, and the
       panel wants "as well as this one". */
    otherDevices: existing ? Math.max(0, status.devices - 1) : status.devices,
  };
}

/** This browser's subscription, if it has one. Null rather than throwing. */
async function currentSubscription(): Promise<PushSubscription | null> {
  try {
    const worker = await registration();
    return await worker.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Ask for permission and sign this browser up.
 *
 * Returns the resulting availability so the caller can say something specific
 * -- a refused prompt leaves permission at `default`, not `denied`, and reads
 * as "off" rather than as an error.
 *
 * **`requestPermission` comes before anything awaited, and must stay there.**
 * Browsers only allow the prompt while the click that asked for it is still
 * "live", and an `await` in front of it spends that -- so fetching the VAPID
 * key first, which is the obvious order to write this in, silently costs the
 * gesture. Safari refuses outright, which would break the platform this feature
 * is most for: iOS is the one where push *requires* the app to be installed.
 *
 * Only the synchronous checks may precede it. The server-configured case is
 * already handled before anybody sees a button -- `pushState` reports
 * `not-configured` and the panel renders a sentence instead -- so nothing is
 * lost by not re-checking it here first.
 */
export async function enablePush(): Promise<PushAvailability> {
  if (!pushSupported()) return "unsupported";
  if (isIOS() && !installed()) return "needs-install";

  const permission = await Notification.requestPermission();
  if (permission === "denied") return "denied";
  if (permission !== "granted") return "off";

  const status = await api.pushStatus();
  if (!status.enabled || !status.publicKey) return "not-configured";

  const worker = await registration();

  /*
   * Reuse an existing subscription rather than creating a second one. A browser
   * allows only one per registration, and `subscribe()` on an already-subscribed
   * registration throws if the application server key differs -- which is
   * exactly what happens when the server's VAPID keypair has been replaced. In
   * that case the old one has to go first, or this device can never re-subscribe.
   */
  let subscription = await worker.pushManager.getSubscription();

  if (subscription && !matchesKey(subscription, status.publicKey)) {
    await subscription.unsubscribe();
    subscription = null;
  }

  subscription ??= await worker.pushManager.subscribe({
    /*
     * Required to be true by every browser that implements this, and enforced:
     * a subscription that might deliver a push showing nothing is not something
     * Chrome will hand out. It is honoured -- the worker shows a notification
     * for every push except when a Pulse window is already focused.
     */
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(status.publicKey),
  });

  await api.subscribePush(subscription.toJSON() as PushSubscriptionPayload);

  return "on";
}

/** Turn it off here, in the browser and on the server. */
export async function disablePush(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;

  const { endpoint } = subscription;

  /* The browser first: if the server call fails, a subscription that no longer
     exists is one the next push deletes anyway when the push service returns
     410. The reverse order can leave a live subscription the server has
     forgotten, which is a device that buzzes with no way to stop it. */
  await subscription.unsubscribe();
  await api.unsubscribePush(endpoint);
}

/**
 * Re-tell the server what this browser's subscription is, on every app start.
 *
 * Browsers rotate subscriptions without asking -- Chrome does it when it
 * decides a subscription is stale -- and a rotated one is a new endpoint the
 * server has never seen, so the old row silently stops delivering. Re-sending
 * whatever the browser currently holds keeps the two in step.
 *
 * Deliberately silent, and deliberately does nothing when this browser is not
 * subscribed: it must never prompt, and it must never be the thing that turns
 * push on.
 */
export async function refreshPushSubscription(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== "granted") return;

    const subscription = await currentSubscription();
    if (!subscription) return;

    await api.subscribePush(subscription.toJSON() as PushSubscriptionPayload);
  } catch {
    /* Nothing to tell the user. Push either works next time or the settings
       panel shows it as off, and both are better than an error on a page they
       did not ask anything of. */
  }
}

/** Whether a subscription was minted against the key the server is using now. */
function matchesKey(subscription: PushSubscription, publicKey: string): boolean {
  const existing = subscription.options.applicationServerKey;
  if (!existing) return false;

  const wanted = urlBase64ToUint8Array(publicKey);
  const actual = new Uint8Array(existing);

  if (actual.length !== wanted.length) return false;
  return actual.every((byte, index) => byte === wanted[index]);
}

/**
 * The VAPID public key, as the browser wants it.
 *
 * `applicationServerKey` takes raw bytes, and the key travels as base64url --
 * which is not what `atob` reads. The two substitutions and the padding below
 * are the whole difference between the two alphabets, and getting this wrong
 * produces an `InvalidCharacterError` at subscribe time rather than anything
 * that mentions keys.
 */
function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");

  const raw = window.atob(base64);

  /*
   * Backed by an ArrayBuffer named explicitly, rather than `new Uint8Array(n)`.
   * Since TypeScript 5.7 the array is generic over its buffer, and the plain
   * form widens to `ArrayBufferLike` -- which `applicationServerKey` rejects,
   * because a SharedArrayBuffer is not something it can accept.
   */
  const output = new Uint8Array(new ArrayBuffer(raw.length));

  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);

  return output;
}
